// client/src/components/mischief/Highlight.js
//
// Yellow marker strip behind text. The strip is rotated a degree or
// two, extending slightly past the text on both sides. Text sits on
// top of it, straight and readable. That's the whole trick.
//
// Usage:
//   <p>He plays <Highlight>defense</Highlight> now.</p>
//   <Highlight color="marker">emphasis</Highlight>
//   <Highlight rotate={-2} inset="loose">breathing room</Highlight>

import { useMemo } from 'react';

// Subtler tilt range than Scrawl — the strip is BEHIND the text, so
// too much rotation makes it look like the highlight missed. Never
// perfectly flat (0), because that reads as printed, not drawn.
const TILTS = [-2, -1, 1, 2];
const pickTilt = () => TILTS[Math.floor(Math.random() * TILTS.length)];

// Strip colors. Each is a "soft" or full color from our tokens.
// The strip is intentionally translucent-feeling — we use the soft
// variants (yellowSoft, accentSoft) so text sits on top with plenty
// of contrast.
const COLOR_CLASS = {
  yellow: 'bg-yellow',       // classic highlighter (default)
  marker: 'bg-marker/25',    // muted purple, less shouty
  accent: 'bg-accentSoft',   // org-tinted, quietest option
};

// Inset controls how far the strip extends beyond the text box.
// "tight"  = strip roughly matches text width (precise, disciplined)
// "normal" = strip juts ~4px past each side (default — feels natural)
// "loose"  = strip juts ~8px past each side (energetic, breezy)
// Values are given as inset offsets on the absolutely-positioned strip.
const INSET_STYLE = {
  tight:  { left: '2px',  right: '2px',  top: '25%', bottom: '10%' },
  normal: { left: '-4px', right: '-4px', top: '20%', bottom: '5%'  },
  loose:  { left: '-8px', right: '-8px', top: '15%', bottom: '0%'  },
};

export function Highlight({
  children,
  color = 'yellow',
  rotate,
  inset = 'normal',
  className = '',
}) {
  // Stable tilt across re-renders — same reason as Scrawl.
  const tilt = useMemo(
    () => (typeof rotate === 'number' ? rotate : pickTilt()),
    [rotate]
  );

  const stripStyle = {
    ...(INSET_STYLE[inset] ?? INSET_STYLE.normal),
    transform: `rotate(${tilt}deg)`,
  };

  const stripColor = COLOR_CLASS[color] ?? COLOR_CLASS.yellow;

  return (
    // The wrapper is relative so the absolute strip positions against
    // the text's own bounding box. inline-block so highlighting a word
    // inside a paragraph doesn't break the line flow.
    <span className={`relative inline-block ${className}`}>
      {/* The strip. aria-hidden because it's decorative — screen
          readers should just hear the text, not "highlight highlight." */}
      <span
        aria-hidden="true"
        className={`absolute ${stripColor} rounded-sm pointer-events-none`}
        style={stripStyle}
      />
      {/* The text sits above the strip. z-10 forces stacking above the
          absolute sibling; without it, some browsers put absolute
          siblings on top of static ones. */}
      <span className="relative z-10">{children}</span>
    </span>
  );
}